import React from 'react';
import './Pages.css';

const Terms: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Terms of Service</h1>
        <p className="page-subtitle">Please read these terms carefully before using GET AI PORTRAIT</p>
      </div>

      <div className="page-content legal-content">
        <p className="last-updated">Last updated: October 2025</p>

        <section className="content-section">
          <h2>1. Scope of Application</h2>
          <p>These Terms of Service govern the use of the mobile application GET AI PORTRAIT ("App"), operated by:</p>
          <div className="contact-box">
            <p>
              Dominik Reif<br />
              Waldstraße 14<br />
              91091 Großenseebach<br />
              Germany<br />
              Email: <a href="mailto:support@dreif-ai.com">support@dreif-ai.com</a>
            </p>
          </div>
          <p>
            By downloading, installing, or using the App, you agree to these Terms. If you do not agree,
            you may not use the App.
          </p>
        </section>

        <section className="content-section">
          <h2>2. Description of the App</h2>
          <p>
            GET AI PORTRAIT offers AI-powered portrait generation services that transform user-uploaded
            selfies into artistic portraits across 56+ styles using state-of-the-art artificial intelligence
            (FLUX 1.1 Pro, DALL-E 3, Stable Diffusion).
          </p>
          <p>
            <strong>All portraits are AI-generated artistic interpretations, not real photographs.</strong>
          </p>
          <p>
            The app provides:
          </p>
          <ul>
            <li>Watermarked preview portraits (free to generate)</li>
            <li>Premium portrait unlock (€9.99 per pack, full resolution, no watermark)</li>
            <li>Subscription access (€3.99/month for unlimited portrait generation)</li>
            <li>Gallery management with CloudKit sync</li>
            <li>Native sharing to social media platforms</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>3. Requirements & User Account</h2>
          <p><strong>Minimum age: 18 years</strong></p>
          <p>
            By using the App, you confirm that you are at least 18 years old and have the legal capacity
            to enter into this agreement.
          </p>
          <p>
            Registration is optional; however, certain features (premium unlock, CloudKit sync) may require
            Apple ID authentication.
          </p>
          <p>
            You are responsible for maintaining the confidentiality of your credentials and all activities
            under your account.
          </p>
          <p>
            You agree to use the App only in compliance with applicable law.
          </p>
        </section>

        <section className="content-section">
          <h2>4. Subscriptions and Payments</h2>

          <h3>(a) Billing:</h3>
          <p>
            In-app purchases and subscriptions are billed through your Apple App Store account.
            Apple Inc.'s payment and refund terms apply.
          </p>

          <h3>(b) Pricing:</h3>
          <ul>
            <li><strong>Premium Unlock:</strong> €9.99 per portrait pack (one-time purchase, full resolution)</li>
            <li><strong>Subscription:</strong> €3.99/month for unlimited portrait generation</li>
            <li><strong>Style Packs:</strong> Additional styles at €2.99 each</li>
          </ul>

          <h3>(c) Automatic Renewal:</h3>
          <p>
            Subscriptions automatically renew unless canceled at least 24 hours before the end of the
            current billing period.
          </p>

          <h3>(d) Cancellation:</h3>
          <p>
            You can cancel your subscription at any time in iOS Settings → Apple ID → Subscriptions.
          </p>

          <h3>(e) Price Changes:</h3>
          <p>
            Price changes will be announced within the App or through the App Store and will apply from
            the next billing period.
          </p>
        </section>

        <section className="content-section">
          <h2>5. Right of Withdrawal</h2>
          <p>
            For digital content, the statutory right of withdrawal applies (§ 356 Abs. 5 BGB).
            As premium content (full-resolution portraits) is provided immediately after purchase, the
            right of withdrawal expires once you expressly agree that execution begins before the
            withdrawal period ends.
          </p>
          <p>
            Refunds can be requested through{' '}
            <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer">
              reportaproblem.apple.com
            </a>
            .
          </p>
          <p>
            See our <a href="/refund-policy">Refund Policy</a> for details.
          </p>
        </section>

        <section className="content-section">
          <h2>6. Intellectual Property</h2>

          <h3>App Content & Technology</h3>
          <p>
            All designs, software components, AI models, style templates, and trademarks of the App are
            protected by copyright and owned by Dominik Reif or licensed third parties. You are granted
            a non-exclusive, non-transferable license for personal use. Any distribution, modification,
            or reproduction without written consent is prohibited.
          </p>

          <h3>Your Uploaded Photos</h3>
          <p>
            You retain all rights to your original selfies. By uploading photos, you grant us a limited,
            temporary license to process them for portrait generation. Photos are deleted within 24 hours
            after processing completes.
          </p>

          <h3>AI-Generated Portraits</h3>
          <p>
            You retain full ownership of AI-generated portraits created from your photos after premium
            unlock or subscription purchase. You may use them for personal or commercial purposes.
          </p>
          <p>
            <strong>Important:</strong> AI-generated content may not be protectable under copyright law
            in all jurisdictions, as it is created by algorithms, not human creativity. We do not warrant
            copyright protection for AI-generated portraits.
          </p>
        </section>

        <section className="content-section">
          <h2>7. User-Generated Content</h2>
          <p>
            Your generated portraits, favorites, and gallery remain your property.
          </p>
          <p>
            This content is stored locally on your device (or synced via CloudKit) and is not transmitted
            to the operator except for processing purposes.
          </p>
          <p>
            You are solely responsible for the content you create and share.
          </p>
        </section>

        <section className="content-section">
          <h2>8. Prohibited Uses</h2>
          <p>You may not use the App to:</p>
          <ul>
            <li>Violate laws or third-party rights</li>
            <li>Upload photos of others without their explicit consent</li>
            <li>Upload photos of minors without parental consent</li>
            <li>Create deepfakes, impersonations, or misleading content</li>
            <li>Generate content for harassment, defamation, or harm</li>
            <li>Misrepresent AI-generated portraits as real photographs</li>
            <li>Use portraits for identity fraud, deception, or illegal purposes</li>
            <li>Copy, distribute, or make publicly available the App's AI models or style templates</li>
            <li>Decompile, manipulate, or circumvent security mechanisms of the App</li>
            <li>Deploy automated systems (bots, scrapers, etc.) to generate portraits in bulk</li>
            <li>Violate the EU AI Act or other AI regulations</li>
          </ul>
          <p>
            <strong>Violation of these prohibitions will result in immediate account termination and may
            be reported to authorities.</strong>
          </p>
        </section>

        <section className="content-section">
          <h2>9. Third-Party Services</h2>
          <p>
            The App uses third-party services such as Firebase (Google LLC), Replicate Inc., OpenAI Inc.,
            CloudKit (Apple Inc.), and RevenueCat Inc. These providers support the technical operation,
            AI processing, and stability of the App.
          </p>
          <p>
            Details on data processing can be found in the <a href="/privacy">Privacy Policy</a>.
          </p>
        </section>

        <section className="content-section">
          <h2>10. Liability</h2>
          <p>Dominik Reif is fully liable for damages resulting from:</p>
          <ul>
            <li>Injury to life, body, or health</li>
            <li>Intentional or grossly negligent breaches of duty</li>
          </ul>
          <p>
            For simple negligent breaches of duty, liability is limited to cases where an essential
            contractual obligation has been violated, and only to the foreseeable, typical contractual
            damage. Liability under Product Liability Law remains unaffected.
          </p>

          <h3>AI-Specific Disclaimers:</h3>
          <p>
            We do not guarantee:
          </p>
          <ul>
            <li>Accuracy, likeness, or quality of AI-generated portraits</li>
            <li>Specific outcomes or satisfaction with results</li>
            <li>Absence of biases or errors in AI output</li>
            <li>Uninterrupted or error-free portrait generation</li>
          </ul>
          <p>
            The service is provided "as is" without warranties of any kind. AI technology has inherent
            limitations, and results may vary based on input photo quality and AI model behavior.
          </p>
        </section>

        <section className="content-section">
          <h2>11. Changes to the App and Terms</h2>
          <p>
            The operator may adapt the App or modify features as long as this is reasonable for users.
            Substantial changes to these Terms of Service will be announced at least 30 days in advance
            through the App or via email. If the user does not object within 30 days, the change is
            considered accepted.
          </p>
        </section>

        <section className="content-section">
          <h2>12. Duration and Termination</h2>
          <p>
            You may terminate use of the App at any time by uninstalling it and requesting data deletion
            via our <a href="/data-deletion">Data Deletion</a> page.
          </p>
          <p>
            The operator may block or terminate access if these Terms are violated, particularly in cases
            of prohibited uses, harmful content creation, or misuse of AI technology.
          </p>
        </section>

        <section className="content-section">
          <h2>13. Applicable Law & Jurisdiction</h2>
          <p>
            The law of the Federal Republic of Germany applies. For consumers, the place of jurisdiction
            is their place of residence.
          </p>
        </section>

        <section className="content-section">
          <h2>14. Online Dispute Resolution</h2>
          <p>
            The European Commission provides an online dispute resolution platform:{' '}
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
              https://ec.europa.eu/consumers/odr
            </a>
          </p>
          <p>
            We are neither obligated nor willing to participate in dispute resolution proceedings before
            a consumer arbitration board.
          </p>
        </section>

        <section className="content-section">
          <h2>15. Severability Clause</h2>
          <p>
            If any provision is found to be invalid, the validity of the remaining provisions remains
            unaffected.
          </p>
        </section>

        <section className="content-section">
          <h2>16. AI-Assisted Content (EU AI Act)</h2>

          <h3>Use of AI Systems</h3>
          <p>
            GET AI PORTRAIT uses artificial intelligence (AI) to create portraits from user-uploaded selfies.
            These systems serve exclusively to support and inspire users with artistic content. All AI models
            are operated responsibly and comply with the transparency requirements of Regulation (EU) 2024/1689
            ("EU AI Act").
          </p>

          <h3>Labeling of AI Content</h3>
          <p>
            All AI-generated portraits are clearly labeled as AI-generated in compliance with the EU AI Act.
            Watermarked previews include "AI Generated" labels. Full-resolution portraits include metadata
            indicating AI generation. This labeling fulfills legal transparency obligations and serves to
            identify artificially generated content.
          </p>

          <h3>Copyright and Protectability</h3>
          <p>
            Content generated entirely by AI systems is not protectable as a work under applicable EU copyright
            law, as it does not constitute a personal intellectual creation by a human. However, if you provide
            your own photo uploads, selections, or modifications, a copyrightable work may arise through your
            creative contribution. GET AI PORTRAIT does not warrant copyright protection for AI-generated content.
          </p>

          <h3>Usage Rights for AI Content</h3>
          <p>
            You receive a non-exclusive, perpetual license for all AI portraits generated in the App for private
            or commercial use. You may save, share, and edit AI portraits as long as no third-party rights are
            violated.
          </p>

          <h3>Disclaimer for AI Content</h3>
          <p>
            AI-generated portraits are automatically generated and may contain incomplete, inaccurate, or
            unexpected results. They do not replace professional photography or reliable fact-checking.
            GET AI PORTRAIT is not liable for damages arising from the use of or reliance on AI-generated content.
          </p>

          <h3>Transparency Requirements</h3>
          <p>
            When sharing AI-generated portraits publicly, you agree to:
          </p>
          <ul>
            <li>Disclose that portraits are AI-generated (e.g., #AIGenerated, #AIArt hashtags)</li>
            <li>Not remove or obscure AI generation indicators or metadata</li>
            <li>Not misrepresent AI portraits as real photographs</li>
            <li>Comply with platform-specific AI disclosure requirements</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>17. Biometric Data Consent</h2>
          <p>
            By uploading photos containing faces, you:
          </p>
          <ul>
            <li>Consent to temporary processing of biometric data (facial features) for artistic transformation</li>
            <li>Confirm you have rights to all uploaded photos</li>
            <li>Acknowledge that face detection (via Apple Vision Framework) is used solely for portrait generation</li>
            <li>Understand we do not create facial recognition profiles or databases</li>
            <li>Understand biometric data is deleted immediately after portrait generation completes</li>
          </ul>
          <p>
            See our <a href="/privacy">Privacy Policy</a> for detailed information on biometric data processing.
          </p>
        </section>

        <section className="content-section">
          <h2>18. Contact</h2>
          <p>For questions about these terms:</p>
          <div className="contact-box">
            <p>
              <strong>General Inquiries:</strong><br />
              📧 <a href="mailto:support@dreif-ai.com">support@dreif-ai.com</a>
            </p>
            <p>
              <strong>Legal:</strong><br />
              📧 <a href="mailto:legal@dreif-ai.com">legal@dreif-ai.com</a>
            </p>
            <p>
              <strong>Postal Address:</strong><br />
              Dominik Reif<br />
              Waldstraße 14<br />
              91091 Großenseebach<br />
              Germany
            </p>
          </div>
        </section>

        <p style={{ marginTop: '40px', textAlign: 'center', color: '#999' }}>
          End of Terms of Service
        </p>

        <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
          <p style={{ margin: 0, fontSize: '0.9em', color: '#666' }}>
            ✅ <strong>Legal compliance verified:</strong> GDPR / BGB / EU AI Act<br />
            ✅ <strong>Apple App Store Guidelines:</strong> 5.1.1 & 5.1.2 (Legal)<br />
            ✅ <strong>Transparency, consent, and labeling requirements fulfilled</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
